/* Shared constants + helpers for the purchase screens (list, form, details, bill). */

export const API = process.env.REACT_APP_API_URL;
const IMG_BASE = process.env.REACT_APP_IMAGE_URL || "";

export const STATUSES = ["Pending", "Received", "Cancelled"];
export const PAYMENT_STATUSES = ["Unpaid", "Partially Paid", "Paid"];
export const PAYMENT_METHODS = ["Bank Transfer", "UPI", "Cash", "Cheque", "Card", "Other"];

export const STATUS_STYLE = {
    Pending: { pill: "bg-amber-100 text-amber-700", text: "text-amber-700", bar: "bg-amber-500" },
    Received: { pill: "bg-emerald-100 text-emerald-700", text: "text-emerald-700", bar: "bg-emerald-500" },
    Cancelled: { pill: "bg-red-100 text-red-600", text: "text-red-600", bar: "bg-red-500" },
};

export const PAYMENT_STYLE = {
    Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Partially Paid": "bg-amber-50 text-amber-700 border-amber-200",
    Unpaid: "bg-red-50 text-red-600 border-red-200",
};

/* ---------- numbers & dates ---------- */
export const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const fmtMoney = (n) =>
    `₹${num(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "--";

export const fmtDateTime = (d) =>
    d
        ? `${fmtDate(d)}, ${new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
        : "--";

/** yyyy-mm-dd in the browser's local time (for <input type="date">). */
export const toDateInput = (d = new Date()) => {
    const x = new Date(d);
    const pad = (n) => String(n).padStart(2, "0");
    return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};

export const shortId = (id = "") => String(id).slice(-6).toUpperCase();

export const purchaseNumber = (p) => p?.purchaseNumber || `PUR-${shortId(p?._id)}`;

export const errMsg = (err, fallback) => err?.response?.data?.message || fallback;

/* ---------- items ---------- */
export const productNameOf = (p) => {
    if (!p) return "";
    if (typeof p.name === "string") return p.name;
    return p.name?.en || (p.name && Object.values(p.name).find(Boolean)) || p.sku || "";
};

export const productImageUrl = (p) => {
    const img = p?.images?.[0] ?? p?.image ?? p?.thumbnail;
    const url = typeof img === "string" ? img : img?.url || img?.secure_url;
    if (!url) return null;
    return /^https?:\/\//.test(url) ? url : `${IMG_BASE}${url}`;
};

/** A line is "manual" when it has no catalogue product behind it. */
export const isManualItem = (it) => Boolean(it?.isManual) || !it?.product;

export const itemName = (it) => productNameOf(it?.product) || it?.name || "Unnamed item";
export const itemSku = (it) => it?.product?.sku || it?.sku || "";
export const itemImage = (it) => productImageUrl(it?.product);
export const itemTotal = (it) => (it?.total != null ? num(it.total) : num(it?.quantity) * num(it?.unitCost));

/* ---------- money ---------- */
export const paidAmount = (p) => {
    if (p?.paymentStatus === "Paid") return num(p.totalAmount);
    if (p?.paymentStatus === "Partially Paid") return num(p.amountPaid);
    return 0;
};

export const balanceDue = (p) => Math.max(0, round2(num(p?.totalAmount) - paidAmount(p)));

/** Breakdown for a saved purchase (older purchases only have totalAmount). */
export const getTotals = (p) => {
    const subTotal = p?.subTotal ? num(p.subTotal) : round2((p?.products || []).reduce((s, it) => s + itemTotal(it), 0));
    return {
        subTotal,
        discount: num(p?.discountAmount),
        gstPercent: num(p?.gstPercent),
        tax: num(p?.taxAmount),
        shipping: num(p?.shippingAmount),
        total: num(p?.totalAmount),
    };
};

/** Same maths the server uses, for the live summary in the form. */
export const calcTotals = ({ items, gstPercent, shippingAmount, discountAmount }) => {
    const subTotal = round2(items.reduce((s, it) => s + num(it.quantity) * num(it.unitCost), 0));
    const discount = round2(num(discountAmount));
    const taxable = round2(subTotal - discount);
    const tax = round2((taxable * num(gstPercent)) / 100);
    const shipping = round2(num(shippingAmount));
    return { subTotal, discount, taxable, tax, shipping, total: round2(taxable + tax + shipping) };
};

/** What marking this purchase as received will do to inventory. */
export const stockSummary = (p) => {
    const items = p?.products || [];
    const catalogue = items.filter((it) => !isManualItem(it));
    return {
        units: catalogue.reduce((s, it) => s + num(it.quantity), 0),
        products: catalogue.length,
        manual: items.length - catalogue.length,
    };
};